// Edit a draft recipe version (WP5.2). Only a draft can change: a signed-off version is what
// the baker signed. Saving replaces the draft's content and brings back the server's
// precision warnings and generated statement; sign-off happens on the recipe page.

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  EmptyState,
  PageHeader,
  toast,
} from '@enp/ui';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Lock } from 'lucide-react';
import { useMemo } from 'react';
import { ApiError } from '../../../../../lib/api.js';
import { useCan } from '../../../../../production/parts.js';
import {
  invalidateProduction,
  recipeQuery,
  updateDraft,
} from '../../../../../production/queries.js';
import { recipeProblem } from '../../../../../production/recipe-dialogs.js';
import { draftFromVersion, RecipeEditor } from '../../../../../production/recipe-editor.js';
import { useDocumentTitle } from '../../../../../shell/page.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/recipes/$recipeId/versions/$versionId')({
  loader: async ({ context, params }) => {
    if (!UUID_RE.test(params.recipeId) || !UUID_RE.test(params.versionId)) throw notFound();
    try {
      const recipe = await context.queryClient.ensureQueryData(recipeQuery(params.recipeId));
      if (!recipe.versions.some((v) => v.id === params.versionId)) throw notFound();
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  component: EditDraftPage,
});

const FORM_ID = 'recipe-draft';

function EditDraftPage() {
  const { recipeId, versionId } = Route.useParams();
  const { data: recipe } = useSuspenseQuery(recipeQuery(recipeId));
  const version = recipe.versions.find((v) => v.id === versionId);
  useDocumentTitle(`Edit ${recipe.product.name} recipe`);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canEdit = useCan('recipe.edit', true);
  // The editor starts from the version as loaded; it doesn't reset while typing.
  const initial = useMemo(
    () => draftFromVersion(version ?? null, recipe.product.base_uom),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when another version opens
    [versionId],
  );
  const save = useMutation({
    mutationFn: (draft: Parameters<typeof updateDraft>[2]) =>
      updateDraft(recipeId, versionId, draft),
    onSuccess: async (detail) => {
      queryClient.setQueryData(recipeQuery(recipeId).queryKey, detail);
      await invalidateProduction(queryClient);
      const saved = detail.versions.find((v) => v.id === versionId);
      toast.success(`Draft version ${saved?.version_no ?? ''} saved`, {
        description:
          saved && saved.warning_count > 0
            ? `${saved.warning_count === 1 ? '1 quantity' : `${saved.warning_count} quantities`} to check before sign-off.`
            : 'Ask the baker to sign it off when it’s ready.',
      });
      void navigate({ to: '/recipes/$recipeId', params: { recipeId } });
    },
  });
  // A draft the server refuses says what is wrong with it (UI5A-19).
  const failed = save.isError
    ? recipeProblem(
        save.error,
        'The draft wasn’t saved',
        'Change it above (highlighted), then save again.',
      )
    : null;

  const back = (
    <Button variant="secondary" asChild>
      <Link to="/recipes/$recipeId" params={{ recipeId }}>
        <ArrowLeft aria-hidden />
        Back to the recipe
      </Link>
    </Button>
  );

  const header = (
    <PageHeader
      eyebrow={
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/recipes">Recipes</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/recipes/$recipeId" params={{ recipeId }}>
                  {recipe.product.name}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Version {version?.version_no}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      }
      title={`Edit draft version ${version?.version_no ?? ''}`}
      description={`${recipe.product.name}. Saving keeps it a draft; the baker signs it off on the recipe page.`}
      actions={
        version?.status === 'draft' &&
        canEdit && (
          <Button type="submit" form={FORM_ID} loading={save.isPending}>
            Save draft
          </Button>
        )
      }
    />
  );

  if (!version || version.status !== 'draft' || !canEdit) {
    return (
      <>
        {header}
        <EmptyState
          compact
          icon={Lock}
          title={
            !canEdit
              ? 'You can’t edit recipes'
              : `Version ${version?.version_no ?? ''} can’t be edited`
          }
          description={
            !canEdit
              ? 'Editing recipes needs the “edit recipes” permission and access to every branch. Ask the owner.'
              : 'Only a draft changes. A signed-off or retired version is kept as it was; make a new draft from the recipe page.'
          }
          action={back}
        />
      </>
    );
  }

  return (
    <>
      {header}
      <RecipeEditor
        product={recipe.product}
        initial={initial}
        saved={version.lines}
        generated={version.generated_statement}
        formId={FORM_ID}
        saving={save.isPending}
        problem={failed}
        onSubmit={(draft) => save.mutate(draft)}
        footer={
          <Button variant="secondary" asChild>
            <Link to="/recipes/$recipeId" params={{ recipeId }}>
              Cancel
            </Link>
          </Button>
        }
      />
    </>
  );
}
